import { type RefObject, useLayoutEffect } from 'react';

/**
 * Sizes an element to the room left in the viewport: everything above it (menu,
 * announcement banner) and below it (the main card's margin) is measured, and
 * the remainder is written to the `--fill-height` custom property on the
 * element. The element's CSS decides where to apply it. The page itself then no
 * longer scrolls, so scroll containers inside the element can.
 *
 * The app shell only has a `min-height` of the viewport, so a plain flex or
 * percentage height cannot resolve to the remaining space; measuring is the
 * only way to get it. The element must be allowed to grow (`flex-grow`) so any
 * slack below it ends up inside the element rather than being counted as
 * "below".
 * @param ref the element to size
 */
export function useFillViewportHeight(ref: RefObject<HTMLElement>) {
	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) {
			return;
		}

		const update = () => {
			const rect = element.getBoundingClientRect();
			const top = rect.top + window.scrollY;
			const below = document.documentElement.scrollHeight - (rect.bottom + window.scrollY);
			const height = Math.max(0, window.innerHeight - top - below);
			element.style.setProperty('--fill-height', `${height}px`);
		};

		update();
		window.addEventListener('resize', update);
		// The document grows or shrinks when something above the element appears
		// or disappears (e.g. the announcement banner).
		const observer = new ResizeObserver(update);
		observer.observe(document.body);

		return () => {
			window.removeEventListener('resize', update);
			observer.disconnect();
		};
	}, [ref]);
}
